import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Icon } from 'semantic-ui-react';
import { getDefaultIcon, TOAST_TYPES } from './toastConfig';
import './Toast.scss';

const useAutoDismiss = (duration, onDismiss, id, isPaused) => {
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const remainingTimeRef = useRef(duration);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (duration <= 0 || !onDismiss) return;

    // Initialize on first mount only
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      remainingTimeRef.current = duration;
      startTimeRef.current = Date.now();
      
      // Start initial timer
      timerRef.current = setTimeout(() => {
        if (onDismiss) {
          onDismiss();
        }
      }, duration);
      
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }

    // Handle pause/resume after initialization
    if (isPaused) {
      // Pause: calculate remaining time and clear timer
      if (timerRef.current && startTimeRef.current) {
        const elapsed = Date.now() - startTimeRef.current;
        remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    } else {
      // Resume: restart timer with remaining time
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      
      if (remainingTimeRef.current > 0 && !timerRef.current) {
        startTimeRef.current = Date.now();
        timerRef.current = setTimeout(() => {
          if (onDismiss) {
            onDismiss();
          }
        }, remainingTimeRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [duration, id, isPaused, onDismiss]);
};

const useMountStatus = () => {
  const isMountedRef = useRef(true);
  
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  return isMountedRef;
};

const renderMessage = (message) => {
  if (!message) return <p>No message</p>;
  if (typeof message === 'string') return <p>{message}</p>;
  if (React.isValidElement(message)) return message;
  if (typeof message === 'function') return React.createElement(message);
  return <p>{String(message)}</p>;
};

const Toast = ({ 
  id, 
  message, 
  type = TOAST_TYPES.INFO, 
  duration = 5000, 
  onClose, 
  className = '', 
  style = {},
  showIcon = true,
  showProgressBar = true,
  clickToClose = true,
  icon,
  config
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isMountedRef = useMountStatus();
  const exitTimerRef = useRef(null);
  const hasCalledCloseRef = useRef(false);

  useEffect(() => {
    // Mark as entered after initial mount to prevent re-animation on re-renders
    const enterTimer = setTimeout(() => {
      setHasEntered(true);
    }, 50);
    
    return () => {
      clearTimeout(enterTimer);
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
  }, []);

  const handleClose = useCallback(() => {
    if (!isMountedRef.current || hasCalledCloseRef.current) return;
    hasCalledCloseRef.current = true;
    setIsExiting(true);
    const exitDuration = config?.animations?.exitDuration || 300;
    exitTimerRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      setIsVisible(false);
      if (onClose) {
        onClose(id);
      }
      exitTimerRef.current = null;
    }, exitDuration);
  }, [id, onClose, config]);

  useAutoDismiss(duration, () => {
    if (isMountedRef.current && !hasCalledCloseRef.current) {
      handleClose();
    }
  }, id, isHovered);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  if (!isVisible) {
    return null;
  }

  const getIcon = () => icon || config?.icons?.[type] || getDefaultIcon(type);
  const typeClass = `kn-toast-type-${type}`;
  // Only show entering animation on initial mount, not on re-renders
  const animationClass = isExiting ? 'kn-toast-exiting' : (hasEntered ? '' : 'kn-toast-entering');
  const combinedClassName = `kn-toast ${typeClass} ${className} ${animationClass}`.trim();
  
  // Merge custom styles with type-specific styles
  const typeStyles = config?.styles?.[type] || {};
  const dynamicStyle = { 
    ...style, 
    ...typeStyles,
  };

  return (
    <div 
      className={combinedClassName}
      style={dynamicStyle}
      onClick={clickToClose ? handleClose : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-toast-id={id}
    >
      <div className="kn-toast-content">
        {showIcon && (
          <div className="kn-toast-icon">
            <Icon name={getIcon()} />
          </div>
        )}
        <div className="kn-toast-message">
          {renderMessage(message)}
        </div>
        <button 
          className="kn-toast-close"
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          aria-label="Close"
        >
          <Icon name="close" />
        </button>
      </div>
      {duration > 0 && showProgressBar && (
        <div className="kn-toast-progress">
          <div 
            className="kn-toast-progress-bar"
            style={{ 
              animationDuration: `${duration}ms`,
              animationTimingFunction: config?.animations?.easing || 'linear',
              animationPlayState: isHovered ? 'paused' : 'running'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Toast;

